import {FormConfig} from '../../form/form.config';
import {FormModelText} from '../../form/form.model.text';
import {FormModel} from '../../form/form.model.abstract';
import {UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {FormModelRadio} from '../../form/form.model.radio';
import {ValidationService} from '../../services/validation.service';
import {FormModelHidden} from '../../form/form.model.hidden';
import {BekocheckConstants} from '../bekocheck.constants';
import {FormModelDates} from '../../form/form.model.dates';
import {FormModelDate} from '../../form/form.model.date';
import {FormModelCheckboxes} from '../../form/form.model.checkboxes';
import {EingabeStepComponent} from './eingabe.step.component';
import {BekocheckAnfrage} from "../anfrage/bekocheck.anfrage";

/**
 * Configuration for the input of the first chapter in the bekocheck advisor.
 * @inheritDoc
 */
export class EingabeForm extends FormConfig {

  /**
   * The options for some fields that are dynamic, depending on other form fields
   */
  static readonly options: any = {
    heizungstyp: {
      mehrfamilienhaus: ['zentral', 'dezentral', 'dezentral2'],
      '1-2familienhaus': ['zentral', 'dezentral2']
    },
    kosten_heizung_wasser_erfassungsart: {
      keys: [
        'kosten_fuer_heizung',
        'kosten_fuer_warmwasser',
        'kosten_fuer_heizung_und_warmwasser',
        'kosten_fuer_heizung_und_wasser',
        'keine_angabe'
      ],
      values: [false, false, false, false, false]
    },
    kosten_heizung_wasser_erfassungsart_wwb_dezentral: {
      keys: [
        'kosten_fuer_heizung',
        'kosten_fuer_heizung_und_wasser',
        'keine_angabe'
      ],
      values: [false, false, false]
    },
    // get - Gasetagenheizung, nsh - Nachtspeicherheizung
    kosten_heizung_wasser_erfassungsart_wwb_zentral_mfh_get_nsh: {
      keys: [
        'kosten_fuer_heizung',
        'kosten_fuer_warmwasser',
        'keine_angabe'
      ],
      values: [false, false, false, false, false]
    },
    kosten_kaltwasser_abwasser_erfassungsart: {
      keys: [
        'kosten_fuer_kaltwasser',
        'kosten_fuer_abwasser',
        'kosten_fuer_niederschlagswasserentsorgung',
        'kosten_fuer_kaltwasser_inkl_abwasser',
        'keine_angabe'
      ],
      values: [false, false, false, false, false]
    },
  };

  /**
   * some data is needed here for validation
   */
  public anfrage: BekocheckAnfrage;

  /**
   * (input) data of the user
   */
  stepComponent: EingabeStepComponent;

  /**
   * @inheritDoc
   * @protected
   */
  protected fields: FormModel[] = [
    // basisdaten
    new FormModelRadio('nutzungstyp', ['mieter', 'wohnungseigentuemer'], [Validators.required], null, null, false),
    new FormModelRadio('gebaeudetyp', ['1-2familienhaus', 'mehrfamilienhaus'], [Validators.required], null, null, false),
    new FormModelRadio('heizungstyp', ['zentral', 'dezentral', 'dezentral2'], [Validators.required], null, null, false),
    new FormModelRadio('energietraeger_name', ['erdgas', 'heizoel', 'fernwaerme', 'fluessiggas', 'strom', 'waermepumpe', 'holzpellets', 'unbekannt'], [Validators.required], null, null, false),
    (new FormModelText('gebaeudebaujahr', [ValidationService.buildingYearOrEmpty], null, null, false, 'number')).setClasses('small-field'),
    (new FormModelText('postleitzahl', [Validators.required, ValidationService.postalCode], null, null, false)).setClasses('small-field'),
    new FormModelHidden('ort'),
    (new FormModelText('gebaeude_flaeche', [Validators.required, Validators.min(0)], 'm2', null, false, 'number'))
      .setClasses('small-field'),
    (new FormModelText('wohnung_flaeche', [Validators.required, Validators.min(0)], 'm2', null, false, 'number'))
      .setClasses('small-field'),
    new FormModelRadio('warmwasser_bereitungsart', ['zentral', 'dezentral'], [Validators.required], null, null, false),

    // abrechnung
    new FormModelDates('abrechnungszeitraum', ['abrechnung_von', 'abrechnung_bis'], [Validators.required, ValidationService.dateFormat],
      null, [FormConfig.getFirstDayOfLastYear(), FormConfig.getLastDayOfLastYear()], false),
    new FormModelRadio('mietzeitraum', ['ja', 'nein_spaeter_eingezogen', 'nein_frueher_ausgezogen'], [Validators.required],
      null, null, false),
    new FormModelDate('mietzeitraum_von', [Validators.required, ValidationService.dateFormat], null, null, false),
    new FormModelDate('mietzeitraum_bis', [Validators.required, ValidationService.dateFormat], null, null, false),
    new FormModelText('gebaeude_betriebskosten_gesamt', [Validators.required, Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('wohnung_gesamtkosten', [Validators.required, Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelRadio('wohnung_nachzahlung_erstattung', ['erstattung', 'nachzahlung', 'nein'], [Validators.required],
      null, null, false),
    new FormModelText('wohnung_nachzahlung', [Validators.required, Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('wohnung_erstattung', [Validators.required, Validators.min(0)], 'eur', null, false, 'number'),

    // heizung und wasser
    (new FormModelCheckboxes('kosten_heizung_wasser_erfassungsart', EingabeForm.options.kosten_heizung_wasser_erfassungsart.keys,
      [], null, EingabeForm.options.kosten_heizung_wasser_erfassungsart.values, false)).setTeaser(true),
    (new FormModelText('kosten_heizung', [Validators.required, Validators.min(0)], 'eur', null, false, 'number')).setTeaser(true),
    (new FormModelText('kosten_warmwasser', [Validators.required, Validators.min(0)], 'eur', null, false, 'number')).setTeaser(true),
    (new FormModelText('kosten_heizung_warmwasser', [Validators.required, Validators.min(0)], 'eur', null, false, 'number'))
      .setTeaser(true),
    (new FormModelText('kosten_heizung_wasser', [Validators.required, Validators.min(0)], 'eur', null, false, 'number')).setTeaser(true),
    (new FormModelCheckboxes('kosten_kaltwasser_abwasser_erfassungsart', EingabeForm.options.kosten_kaltwasser_abwasser_erfassungsart.keys,
      [], null, EingabeForm.options.kosten_kaltwasser_abwasser_erfassungsart.values, false)).setTeaser(true),
    (new FormModelText('kosten_kaltwasser', [Validators.required, Validators.min(0)], 'eur', null, false, 'number')).setTeaser(true),
    (new FormModelText('kosten_abwasser', [Validators.required, Validators.min(0)], 'eur', null, false, 'number')).setTeaser(true),
    (new FormModelText('kosten_niederschlagswasserentsorgung', [Validators.required, Validators.min(0)], 'eur', null, false, 'number'))
      .setTeaser(true),
    (new FormModelText('kosten_abwasser_kaltwasser', [Validators.required, Validators.min(0)], 'eur', null, true, 'number'))
      .setTeaser(true),

    // hauswart
    new FormModelText('kosten_hauswart', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_winterdienst', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_gartenpflege', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_gebaeudereinigung', [Validators.min(0)], 'eur', null, false, 'number'),

    // weitere kostenpositionen
    new FormModelText('kosten_grundsteuer', [Validators.min(0)], 'eur', null, false, 'number'),
    (new FormModelText('kosten_muellbeseitigung', [Validators.min(0)], 'eur', null, false, 'number')).setTeaser(true),
    new FormModelText('kosten_schornsteinreinigung', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_strassenreinigung', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_versicherungen', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_antenne_kabel', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_beleuchtung', [Validators.min(0)], 'eur', null, false, 'number'),
    new FormModelText('kosten_aufzug', [Validators.min(0)], 'eur', null, false, 'number'),
    (new FormModelText('kosten_sonstige', [Validators.min(0)], 'eur', null, false, 'number')).setTeaser(true)
  ];

  /**
   * @inheritDoc
   * @protected
   */
  protected formValidators = [
    ValidationService.dateRangeMinMax(
      'abrechnung_von', 'abrechnung_bis', BekocheckConstants.INVOICE_MIN_DAYS, BekocheckConstants.INVOICE_MAX_DAYS
    ),
    ValidationService.dateRange('abrechnung_von','abrechnung_bis'),
    EingabeForm.validateCheckboxes('kosten_heizung_wasser_erfassungsart'),
    EingabeForm.validateCheckboxes('kosten_kaltwasser_abwasser_erfassungsart')
  ];

  /**
   * List of formfields where changes of the value, change other fields.
   */
  fieldsForFormChanges: string[] = [
    'nutzungstyp',
    'gebaeudetyp',
    'heizungstyp',
    'gebaeudebaujahr',
    'warmwasser_bereitungsart'
  ]
    .concat(EingabeForm.getCheckboxesIds('kosten_heizung_wasser_erfassungsart',
      EingabeForm.options.kosten_heizung_wasser_erfassungsart.keys))
    .concat(EingabeForm.getCheckboxesIds('kosten_kaltwasser_abwasser_erfassungsart',
      EingabeForm.options.kosten_kaltwasser_abwasser_erfassungsart.keys));

  /**
   * @inheritDoc
   * @protected
   */
  protected groups = {
    'basisdaten/1': ['nutzungstyp'],
    'basisdaten/2': ['gebaeudetyp'],
    'basisdaten/3': ['heizungstyp'],
    'basisdaten/4': ['energietraeger_name'],
    'basisdaten/5': ['gebaeudebaujahr'],
    'basisdaten/6': ['postleitzahl', 'ort'],
    'basisdaten/7': ['gebaeude_flaeche'],
    'basisdaten/8': ['wohnung_flaeche'],
    'basisdaten/9': ['warmwasser_bereitungsart'],

    'abrechnung/1': ['abrechnungszeitraum'],
    'abrechnung/2': ['mietzeitraum'],
    'abrechnung/3': ['mietzeitraum_von'],
    'abrechnung/4': ['mietzeitraum_bis'],
    'abrechnung/5': ['gebaeude_betriebskosten_gesamt'],
    'abrechnung/6': ['wohnung_gesamtkosten'],
    'abrechnung/7': ['wohnung_nachzahlung_erstattung'],
    'abrechnung/8': ['wohnung_nachzahlung'],
    'abrechnung/9': ['wohnung_erstattung'],

    'heizung-und-wasser/1': ['kosten_heizung_wasser_erfassungsart'],
    'heizung-und-wasser/2': ['kosten_heizung'],
    'heizung-und-wasser/3': ['kosten_warmwasser'],
    'heizung-und-wasser/4': ['kosten_heizung_warmwasser'],
    'heizung-und-wasser/5': ['kosten_heizung_wasser'],
    'heizung-und-wasser/6': ['kosten_kaltwasser_abwasser_erfassungsart'],
    'heizung-und-wasser/7': ['kosten_kaltwasser'],
    'heizung-und-wasser/8': ['kosten_abwasser'],
    'heizung-und-wasser/9': ['kosten_abwasser_kaltwasser'],
    'heizung-und-wasser/10': ['kosten_niederschlagswasserentsorgung'],

    'hauswart/1': ['kosten_hauswart'],
    'hauswart/2': ['kosten_winterdienst'],
    'hauswart/3': ['kosten_gartenpflege'],
    'hauswart/4': ['kosten_gebaeudereinigung'],

    'weitere-kostenpositionen/1': ['kosten_grundsteuer'],
    'weitere-kostenpositionen/2': ['kosten_muellbeseitigung'],
    'weitere-kostenpositionen/3': ['kosten_schornsteinreinigung'],
    'weitere-kostenpositionen/4': ['kosten_strassenreinigung'],
    'weitere-kostenpositionen/5': ['kosten_versicherungen'],
    'weitere-kostenpositionen/6': ['kosten_antenne_kabel'],
    'weitere-kostenpositionen/7': ['kosten_beleuchtung'],
    'weitere-kostenpositionen/8': ['kosten_aufzug'],
    'weitere-kostenpositionen/9': ['kosten_sonstige'],

  };

  /**
   * Validates checkboxes by id, at least one checkbox must be selected.
   */
  static validateCheckboxes(id: string): ValidatorFn {
    return (formGroup: UntypedFormGroup) => {
      let validate = false;
      const fieldIds = EingabeForm.getCheckboxesIds(id, EingabeForm.options[id].keys);
      fieldIds.forEach((fieldId) => {
        if (typeof formGroup.controls[fieldId] !== 'undefined') {
          validate = true;
        }
      });
      if (validate) {
        let valid = false;
        fieldIds.forEach((fieldId) => {
          if (formGroup.controls[fieldId]?.value === true) {
            valid = true;
          }
        });
        if (!valid) {
          const result = {};
          result[this.camelize(id)] = false;
          return result;
        }
      }

      // everything is fine, of the fields are not theire, of they have no values
      return null;
    };
  }

  /**
   * Converts a string into camelCase (used to build validator error keys)
   * @param string
   */
  static camelize(string: string): string {
    return string
      .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
      .replace(/^(.)/, m => m.toLowerCase());
  }

  /**
   * Returns ids for checkboxes
   * @param id
   * @param keys
   */
  static getCheckboxesIds(id: string, keys: string[]) {
    const result = [];
    keys.forEach((key) => {
      result.push(id + '_' + key);
    });
    return result;
  }

  /**
   * Sets stepComponent
   * @param stepComponent
   */
  setStepComponent(stepComponent: EingabeStepComponent): void {
    this.stepComponent = stepComponent;
  }

  /**
   * Compares two arrays
   * @param a
   * @param b
   */
  compareArrays = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  /**
   * Any time a field is updated, this method is called.
   * @param values
   */
  updateFields(values: { [key: string]: any }): any {
    // changes based on field 'nutzungstyp'
    if (values.hasOwnProperty('nutzungstyp') === true) {
      if (values.nutzungstyp === 'wohnungseigentuemer') {
        this.deactivateField('kosten_grundsteuer');
        values.kosten_grundsteuer = null;
      }
      else {
        this.activateField('kosten_grundsteuer');
      }
    }

    // changes based on field 'gebaeudetyp'
    if (values.hasOwnProperty('gebaeudetyp') === true) {
      // set options for heating type
      this.setOptions('heizungstyp', EingabeForm.options.heizungstyp[values.gebaeudetyp]);

      if (values.gebaeudetyp === '1-2familienhaus') {
        this.setFieldValidators('gebaeude_flaeche', [Validators.required, ValidationService.buildingAreaOneFamilyHouse]);
        this.getField('gebaeude_flaeche').setErrorId('gebaeude_flaeche.efh');
      }
      else {
        this.setFieldValidators('gebaeude_flaeche', [Validators.required, ValidationService.buildingAreaMultiFamilyHouse]);
        this.getField('gebaeude_flaeche').setErrorId('gebaeude_flaeche.mfh');
      }
      this.setFieldValidators('wohnung_flaeche', [
        Validators.required, Validators.min(BekocheckConstants.APARTMENT_AREA_MIN), Validators.max(values.gebaeude_flaeche)
      ]);
      this.getField('wohnung_flaeche').setErrorId('wohnung_flaeche');
    }

    // changes based on field 'heizungstyp'
    if (values.hasOwnProperty('heizungstyp') === true) {
      if (values.heizungstyp === 'dezentral') {
        // case 1: show field for inhabitants and update the field validator
        this.deactivateField('energietraeger_name');
        values.energietraeger_name = 'erdgas';
      }
      else if (values.heizungstyp === 'dezentral2') {
        // case 1: show field for inhabitants and update the field validator
        this.deactivateField('energietraeger_name');
        values.energietraeger_name = 'strom';
      }
      else {
        this.activateField('energietraeger_name');
      }
    }

    // changes based on field 'gebaeudebaujahr'
    if (values.hasOwnProperty('gebaeudebaujahr') === true) {
      const min = (values.gebaeudebaujahr === '' || values.gebaeudebaujahr === null)
        ? ((new Date()).getFullYear() - 14) : (values.gebaeudebaujahr + 1);
      const previousYear = (new Date()).getFullYear() - 1;
      this.setFieldValidators('abrechnung_von', [
        Validators.required,
        ValidationService.dateFormat,
        ValidationService.dateYearMinMax(min, previousYear)
      ]);
      this.setFieldValidators('abrechnung_bis', [
        Validators.required,
        ValidationService.dateFormat,
        ValidationService.dateYearMinMax(min, previousYear + 1)
      ]);
    }

    // changes based on field 'warmwasser_bereitungsart'
    if (values.hasOwnProperty('warmwasser_bereitungsart') === true) {
      const currentOptions = this.getField('kosten_heizung_wasser_erfassungsart').getOptions();
      if (values.warmwasser_bereitungsart === 'dezentral') {
        if (this.compareArrays(currentOptions, EingabeForm.options.kosten_heizung_wasser_erfassungsart_wwb_dezentral.keys) !== true) {
          this.setOptions('kosten_heizung_wasser_erfassungsart',
            EingabeForm.options.kosten_heizung_wasser_erfassungsart_wwb_dezentral.keys);
          values.kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser = false;
          values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser = false;
        }
      }
      else {
        if (this.anfrage?.userInput?.gebaeudetyp === 'mehrfamilienhaus'
          && (this.anfrage?.userInput?.heizungstyp === 'dezentral' || this.anfrage?.userInput?.heizungstyp === 'dezentral2')) {
          if (this.compareArrays(currentOptions,
            EingabeForm.options.kosten_heizung_wasser_erfassungsart_wwb_zentral_mfh_get_nsh.keys) !== true) {
            this.setOptions('kosten_heizung_wasser_erfassungsart',
              EingabeForm.options.kosten_heizung_wasser_erfassungsart_wwb_zentral_mfh_get_nsh.keys);
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser = false;
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser = false;
          }
        }
        else {
          if (this.compareArrays(currentOptions, EingabeForm.options.kosten_heizung_wasser_erfassungsart.keys) !== true) {
            this.setOptions('kosten_heizung_wasser_erfassungsart', EingabeForm.options.kosten_heizung_wasser_erfassungsart.keys);
          }
        }
      }
    }

    // changes based on field 'mietzeitraum'
    if (values.hasOwnProperty('mietzeitraum') === true) {
      if (values.mietzeitraum === 'ja') {
        this.deactivateField('mietzeitraum_von');
        this.deactivateField('mietzeitraum_bis');
      }
      else if (values.mietzeitraum === 'nein_spaeter_eingezogen') {
        this.activateField('mietzeitraum_von');
        this.setFieldValidators('mietzeitraum_von', [
          Validators.required,
          ValidationService.dateFormat,
          ValidationService.dateBetween(values.abrechnung_von, values.abrechnung_bis)
        ]);
        this.deactivateField('mietzeitraum_bis');
      }
      else if (values.mietzeitraum === 'nein_frueher_ausgezogen') {
        this.deactivateField('mietzeitraum_von');
        this.activateField('mietzeitraum_bis');
        this.setFieldValidators('mietzeitraum_bis', [
          Validators.required,
          ValidationService.dateFormat,
          ValidationService.dateBetween(values.abrechnung_von, values.abrechnung_bis)
        ]);
      }
    }

    // changes based on field 'wohnung_nachzahlung_erstattung'
    if (values.hasOwnProperty('wohnung_nachzahlung_erstattung') === true) {
      if (values.wohnung_nachzahlung_erstattung === 'nein') {
        this.deactivateField('wohnung_nachzahlung');
        values.wohnung_nachzahlung = null;
        this.deactivateField('wohnung_erstattung');
        values.wohnung_erstattung = null;
      }
      else if (values.wohnung_nachzahlung_erstattung === 'nachzahlung') {
        this.activateField('wohnung_nachzahlung');
        this.deactivateField('wohnung_erstattung');
        values.wohnung_erstattung = null;
      }
      else if (values.wohnung_nachzahlung_erstattung === 'erstattung') {
        this.deactivateField('wohnung_nachzahlung');
        values.wohnung_nachzahlung = null;
        this.activateField('wohnung_erstattung');
      }
    }

    // changes based on checkboxes kosten_heizung_wasser_erfassungsart
    this.updateFieldsByCheckboxId(values, 'kosten_heizung_wasser_erfassungsart');
    // changes based on field 'kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung'
    if (values.hasOwnProperty('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung') === true) {
      if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung === true) {
        this.activateField('kosten_heizung');
      }
      else {
        this.deactivateField('kosten_heizung');
        values.kosten_heizung = null;
      }
    }

    // changes based on field 'kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser'
    if (values.hasOwnProperty('kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser') === true) {
      if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser === true) {
        this.activateField('kosten_warmwasser');
      }
      else {
        this.deactivateField('kosten_warmwasser');
        values.kosten_warmwasser = null;
      }
    }

    // changes based on field 'kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser'
    if (values.hasOwnProperty('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser') === true) {
      if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser === true) {
        this.activateField('kosten_heizung_warmwasser');
      }
      else {
        this.deactivateField('kosten_heizung_warmwasser');
        values.kosten_heizung_warmwasser = null;
      }
    }

    // changes based on field 'kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser'
    if (values.hasOwnProperty('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser') === true) {
      if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser === true) {
        this.activateField('kosten_heizung_wasser');
        // deactivate kosten_kaltwasser_abwasser_erfassungsart
        this.deactivateField('kosten_kaltwasser_abwasser_erfassungsart');
        values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser = false;
        values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser = false;
        values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung = false;
        values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser = false;
        values.kosten_kaltwasser_abwasser_erfassungsart_keine_angabe = false;
      }
      else {
        this.deactivateField('kosten_heizung_wasser');
        values.kosten_heizung_wasser = null;
        // activate kosten_kaltwasser_abwasser_erfassungsart
        this.activateField('kosten_kaltwasser_abwasser_erfassungsart');
      }
    }

    // changes based on checkboxes kosten_kaltwasser_abwasser_erfassungsart
    this.updateFieldsByCheckboxId(values, 'kosten_kaltwasser_abwasser_erfassungsart');
    if (values.hasOwnProperty('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser') === true &&
      values.hasOwnProperty('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser') === true &&
      values.hasOwnProperty('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung') === true) {
      if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser === true) {
        this.activateField('kosten_kaltwasser');
      }
      else {
        this.deactivateField('kosten_kaltwasser');
        values.kosten_kaltwasser = null;
      }
      if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser === true) {
        this.activateField('kosten_abwasser');
      }
      else {
        this.deactivateField('kosten_abwasser');
        values.kosten_abwasser = null;
      }
      if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung === true) {
        this.activateField('kosten_niederschlagswasserentsorgung');
      }
      else {
        this.deactivateField('kosten_niederschlagswasserentsorgung');
        values.kosten_niederschlagswasserentsorgung = null;
      }
    }

    // changes based on field 'kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser'
    if (values.hasOwnProperty('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser') === true) {
      if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser === true) {
        this.activateField('kosten_abwasser_kaltwasser');
      }
      else {
        this.deactivateField('kosten_abwasser_kaltwasser');
        values.kosten_abwasser_kaltwasser = null;
      }
    }

    if (values.hasOwnProperty('heizungstyp') === true) {
      if (values.heizungstyp != 'zentral') {
        this.deactivateField('warmwasser_bereitungsart');
        this.deactivateField('kosten_heizung_wasser_erfassungsart');
        this.deactivateField('kosten_warmwasser')
        this.deactivateField('kosten_heizung');
        this.deactivateField('kosten_heizung_warmwasser');
        this.deactivateField('kosten_heizung_wasser');
        values.warmwasser_bereitungsart = null;
        values.kosten_heizung_wasser_erfassungsart = null;
        values.kosten_warmwasser = null;
        values.kosten_heizung = null;
        values.kosten_heizung_warmwasser = null;
        values.kosten_heizung_wasser = null;
      }
    }


    return values;
  }

  updateFieldsByCheckboxId(values, id) {
    let update = false;
    const fieldIds = EingabeForm.getCheckboxesIds(id, EingabeForm.options[id].keys);
    fieldIds.forEach((fieldId) => {
      if (values.hasOwnProperty(fieldId) === true) {
        update = true;
      }
    });

    switch (id) {
      case 'kosten_heizung_wasser_erfassungsart':
        if (update) {
          // kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung
          if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser === true ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser === true ||
            values.kosten_heizung_wasser_erfassungsart_keine_angabe === true
          ) {
            this.disableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung');
          }
          else {
            this.enableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung');
          }

          // kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser
          if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser === true ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser === true ||
            values.kosten_heizung_wasser_erfassungsart_keine_angabe === true
          ) {
            this.disableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser');
          }
          else {
            this.enableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser');
          }

          // kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser
          if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung === true ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser === true ||
            values.kosten_heizung_wasser_erfassungsart_keine_angabe === true
          ) {
            this.disableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser');
          }
          else {
            this.enableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser');
          }

          // kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser
          if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung === true ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser === true ||
            values.kosten_heizung_wasser_erfassungsart_keine_angabe === true
          ) {
            this.disableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser');
          }
          else {
            this.enableSubfield('kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser');
          }

          // kosten_heizung_wasser_erfassungsart_keine_angabe
          if (values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung === true ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_warmwasser ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_warmwasser === true ||
            values.kosten_heizung_wasser_erfassungsart_kosten_fuer_heizung_und_wasser === true
          ) {
            this.disableSubfield('kosten_heizung_wasser_erfassungsart_keine_angabe');
          }
          else {
            this.enableSubfield('kosten_heizung_wasser_erfassungsart_keine_angabe');
          }
        }
        break;

      case 'kosten_kaltwasser_abwasser_erfassungsart':
        if (update) {
          // kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser
          if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser === true ||
            values.kosten_kaltwasser_abwasser_erfassungsart_keine_angabe === true) {
            this.disableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser');
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser = false;
          }
          else {
            this.enableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser');
          }

          // kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser
          if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser === true ||
            values.kosten_kaltwasser_abwasser_erfassungsart_keine_angabe === true) {
            this.disableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser');
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser = false;
          }
          else {
            this.enableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser');
          }

          // kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung
          if (values.kosten_kaltwasser_abwasser_erfassungsart_keine_angabe === true) {
            this.disableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung');
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung = false;
          }
          else {
            this.enableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung');
          }

          // kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser
          if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser === true ||
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser === true ||
            values.kosten_kaltwasser_abwasser_erfassungsart_keine_angabe === true) {
            this.disableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser');
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser = false;
          }
          else {
            this.enableSubfield('kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser');
          }

          // kosten_kaltwasser_abwasser_erfassungsart_keine_angabe
          if (values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser === true ||
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_abwasser === true ||
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_niederschlagswasserentsorgung === true ||
            values.kosten_kaltwasser_abwasser_erfassungsart_kosten_fuer_kaltwasser_inkl_abwasser === true) {
            this.disableSubfield('kosten_kaltwasser_abwasser_erfassungsart_keine_angabe');
            values.kosten_kaltwasser_abwasser_erfassungsart_keine_angabe = false;
          }
          else {
            this.enableSubfield('kosten_kaltwasser_abwasser_erfassungsart_keine_angabe');
          }
        }
        break;
    }
  }

  /**
   * In this function all dynamic changes of the form is done.
   * Based on selection options or value other fields are shown/hidden, options changed and validators adapted.
   *
   * @inheritDoc
   * @param values
   * @protected
   */
  protected onSetData(values: { [key: string]: any }): void {
    this.updateFields(values);
  }

  /**
   * @inheritDoc
   * @protected
   */
  protected onChange() {
    // check if the form has some fields which value changes other fields.
    if (this.fieldsForFormChanges.find(field => this.form.get(field)) !== undefined) {
      const newValues = this.updateFields(this.getData());
      this.stepComponent.saveData(newValues);
    }
  }
}
